import React, { useState, useEffect, useContext } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { API, graphqlOperation } from "aws-amplify";
import { getMirthMessageDetails } from "graphql/queries.js";

// react-bootstrap components
import {
    Container,
    Row,
    Col,
    Table,
    Tooltip,
    OverlayTrigger,
    Card,
    Spinner,
} from "react-bootstrap";

const zindex = {
    zIndex: "9999",
};
const ellipsisStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "300px",
};
const noMargin = {
    margin: 0,
};

function MessageDetails() {
    const { taskId, messageId } = useParams();
    const location = useLocation();
    const notify = useContext(NotificationAlertContext);
    const history = useHistory();

    // Get parameters from navigation state or URL params
    const stateProps = location.state || {};
    const searchParams = new URLSearchParams(location.search);

    // Extract required parameters with fallback priority
    const clientEnvId = stateProps.clientEnvironmentId || searchParams.get('clientEnvironmentId') || "";
    const cloudTaskId = stateProps.cloudTaskId || searchParams.get('cloudTaskId') || taskId || "";
    const dateRollup = stateProps.dateRollup || searchParams.get('dateRollup') || "";
    const channelId = stateProps.channelId || searchParams.get('channelId') || "";
    const msgId = stateProps.messageId || searchParams.get('messageId') || messageId || 0;

    const [messageData, setMessageData] = useState({
        task_id: "",
        client_name: "",
        client_environment_name: "",
        channel_name: "",
        task_type: "",
        message_status: "",
        message_id: "",
        connectors: []
    });
    const [loading, setLoading] = useState(true);

    // Fetch message details using GraphQL
    const fetchMessageDetails = async () => {
        try {
            setLoading(true);

            if (!clientEnvId || !cloudTaskId || !dateRollup || !channelId || !msgId) {
                notify("danger", "Missing required parameters for message details");
                history.push("/admin/mirth-tasks");
                return false;
            }

            const response = await API.graphql(
                graphqlOperation(getMirthMessageDetails, {
                    clientEnvironmentId: clientEnvId,
                    cloudTaskId: cloudTaskId,
                    dateRollup: dateRollup,
                    channelId: channelId,
                    messageId: parseInt(msgId)
                })
            );

            const result = response.data.getMirthMessageDetails;

            if (result.success && result.body) {
                // Transform the API response to match the UI structure
                const transformedData = {
                    task_id: result.body.task_id,
                    client_name: result.body.client_name || "Unknown Client",
                    client_environment_name: result.body.environment_name || "Unknown Environment",
                    channel_name: result.body.channel_name || "Unknown Channel",
                    task_type: "Message Resend",
                    message_status: result.body.message_status,
                    message_id: result.body.message_id,
                    connectors: result.body.connector_details?.map((connector, index) => ({
                        connector_id: connector.connector_id,
                        connector_status: connector.connector_status,
                        connector_error_text: connector.connector_error_text || ""
                    })) || []
                };

                setMessageData(transformedData);
                notify("success", "Message details loaded successfully");
                return true;
            } else {
                notify("danger", result.message || "Failed to load message details");
                return false;
            }
        } catch (error) {
            console.error("Error fetching message details:", error);
            notify("danger", `Failed to load message details: ${error.message}`);
            return false;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        async function fetchData() {
            if (clientEnvId && cloudTaskId && dateRollup && channelId && msgId) {
                const isSuccess = await fetchMessageDetails();
                if (!isSuccess) {
                    notify("danger");
                }
            } else {
                notify("danger", "Missing required parameters for message details");
                history.push("/admin/mirth-tasks");
            }
        }
        fetchData();
    }, [clientEnvId, cloudTaskId, dateRollup, channelId, msgId]);

    if (loading) return <Spinner animation="grow" />;

    return (
        <>
            <Container fluid>
                <Row>
                    <Col>
                        <h2>Message Details</h2>
                    </Col>
                </Row>
                <Card id="client-details-card">
                    <Card.Body>
                        <Row>
                            <Col md="12">
                                <h3>Client Details</h3>
                                <p style={noMargin}>Task Id: {messageData.task_id}</p>
                                <p style={noMargin}>Task Type: {messageData.task_type}</p>
                                <p style={noMargin}>Client Name: {messageData.client_name}</p>
                                <p style={noMargin}>Client Environment Name: {messageData.client_environment_name}</p>
                                <p style={noMargin}>Channel Name: {messageData.channel_name}</p>
                                <p style={noMargin}>Message Id: {messageData.message_id}</p>
                                <p style={noMargin}>Status: {messageData.message_status}</p>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>


                <Card>
                    <Card.Body>
                        <Row>
                            <Col md="12" id="connector-details">
                                <h3>Connector Details</h3>
                                {messageData.connectors && messageData.connectors.length > 0 && (
                                    <>
                                        <Table className="table-hover table-striped w-full">
                                            <thead>
                                                <tr>
                                                    <th>Connector ID</th>
                                                    <th>Connector Message Status</th>
                                                    <th>Connector Error Text</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {messageData.connectors.map((connector, key) => {
                                                    return (
                                                        <tr key={key}>
                                                            <td>{connector.connector_id}</td>
                                                            <td>{connector.connector_status}</td>
                                                            <td>
                                                                {
                                                                    <OverlayTrigger
                                                                        key={`event-type-${connector.connector_id}`}
                                                                        placement="right"
                                                                        overlay={
                                                                            <Tooltip
                                                                                id={`event-type-tooltip-${connector.connector_id}`}
                                                                                style={zindex}
                                                                            >
                                                                                {connector.connector_error_text}
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <div style={ellipsisStyle}>
                                                                            {connector.connector_error_text
                                                                                ? connector.connector_error_text
                                                                                : ""}
                                                                        </div>
                                                                    </OverlayTrigger>
                                                                }
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </>
                                )}
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
}

export default MessageDetails;
